export interface AlliedCourseInfo {
  name: string;
  shortCode: string;
  description: string;
  iconName: "Stethoscope" | "FlaskConical" | "ShieldPlus" | "Wind" | "HeartPulse" | "Therapy";
  defaultSeats: {
    UR: number;
    OBC: number;
    SC: number;
    ST: number;
    EWS: number;
  };
  femaleSeats?: number;
  pwdSeats?: number;
}

export const DEFAULT_ALLIED_COURSES: AlliedCourseInfo[] = [
  {
    name: "B.Sc. in Emergency Medical Technology",
    shortCode: "B.Sc. EMT",
    description: "Emergency care, trauma management, patient stabilisation, and life-saving clinical support in critical situations.",
    iconName: "Stethoscope",
    defaultSeats: { UR: 8, OBC: 5, SC: 3, ST: 1, EWS: 2 },
    femaleSeats: 3,
    pwdSeats: 1,
  },
  {
    name: "B.Sc. in Hematology & Transfusion Medicine",
    shortCode: "B.Sc. HTM",
    description: "Blood testing, transfusion practices, laboratory diagnostics, and patient safety in modern healthcare.",
    iconName: "FlaskConical",
    defaultSeats: { UR: 8, OBC: 5, SC: 3, ST: 1, EWS: 2 },
    femaleSeats: 3,
    pwdSeats: 1,
  },
  {
    name: "B.Sc. in CSSD Technology",
    shortCode: "B.Sc. CSSD",
    description: "Sterilisation techniques, infection control, medical equipment processing, and hospital safety standards.",
    iconName: "ShieldPlus",
    defaultSeats: { UR: 8, OBC: 5, SC: 3, ST: 1, EWS: 2 },
    femaleSeats: 3,
    pwdSeats: 1,
  },
  {
    name: "B.Sc. in Respiratory Technology",
    shortCode: "B.Sc. RT",
    description: "Respiratory care, ventilator management, pulmonary diagnostics, and patient support in critical care settings.",
    iconName: "Wind",
    defaultSeats: { UR: 8, OBC: 5, SC: 3, ST: 1, EWS: 2 },
    femaleSeats: 3,
    pwdSeats: 1,
  },
  {
    name: "B.Sc. in Cardio Vascular Technology",
    shortCode: "B.Sc. CVT",
    description: "Specialized training in cardiac diagnostics, cardiovascular procedures, patient monitoring, and advanced heart care.",
    iconName: "HeartPulse",
    defaultSeats: { UR: 8, OBC: 5, SC: 3, ST: 1, EWS: 2 },
    femaleSeats: 3,
    pwdSeats: 1,
  },
  {
    name: "Bachelor of Radiation Therapy Technology (BRTT)",
    shortCode: "B.Sc. BRTT",
    description: "Specialized training in radiation therapy, radiation safety, and patient care in radiation therapy settings.",
    iconName: "Therapy",
    defaultSeats: { UR: 2, OBC: 2, SC: 1, ST: 0, EWS: 0 },
    femaleSeats: 0,
    pwdSeats: 0,
  }
];

/**
 * Maps course code/alias/shortcode or full name to clean display title.
 */
export const getCourseDisplayName = (val?: string | string[]): string => {
  if (!val) return "N/A";
  if (Array.isArray(val)) {
    return val.map((item) => getCourseDisplayName(item)).join(", ");
  }

  const normalized = val.trim();

  // Known legacy mappings & abbreviations
  const courseMap: Record<string, string> = {
    "BSc EMT": "B.Sc. in Emergency Medical Technology",
    "B.Sc. EMT": "B.Sc. in Emergency Medical Technology",
    "EMT": "B.Sc. in Emergency Medical Technology",

    "BSc HTM": "B.Sc. in Hematology & Transfusion Medicine",
    "B.Sc. HTM": "B.Sc. in Hematology & Transfusion Medicine",
    "HTM": "B.Sc. in Hematology & Transfusion Medicine",

    "BSc CSSD": "B.Sc. in CSSD Technology",
    "B.Sc. CSSD": "B.Sc. in CSSD Technology",
    "CSSD": "B.Sc. in CSSD Technology",

    "BSc RT": "B.Sc. in Respiratory Technology",
    "B.Sc. RT": "B.Sc. in Respiratory Technology",
    "RT": "B.Sc. in Respiratory Technology",

    "BSc CVT": "B.Sc. in Cardio Vascular Technology",
    "B.Sc. CVT": "B.Sc. in Cardio Vascular Technology",
    "CVT": "B.Sc. in Cardio Vascular Technology",

    "BRTT": "B.Sc. in Radiation Therapy Technology",
    "B.Sc. BRTT": "B.Sc. in Radiation Therapy Technology",
    "B.R.T.T.": "B.Sc. in Radiation Therapy Technology",

    "Both": "Both Courses (B.Sc. EMT & B.Sc. HTM)",
    "All": "All Allied Health Sciences Courses",
  };

  if (courseMap[normalized]) return courseMap[normalized];

  // Try matching against DEFAULT_ALLIED_COURSES names or short codes
  const match = DEFAULT_ALLIED_COURSES.find(
    (c) => c.name.toLowerCase() === normalized.toLowerCase() || c.shortCode.toLowerCase() === normalized.toLowerCase()
  );

  return match ? match.name : normalized;
};
